//@ts-nocheck

import styled, { css } from 'styled-components'
import { primaryFont, getFontClassName } from '../../../theme/fonts'

interface ThemeProps {
  theme: {
    styledVariant: (prop: string, options: Record<string, any>) => any;
    typo: (value: string) => any;
    rem: (value: number) => string;
  }
}

interface TypographyProps extends ThemeProps {
  uppercase?: boolean;
  capitalize?: boolean;
  colorScheme?: string;
  typo?: string;
  fontPrimary?: boolean;
  weight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'extraBold';
  textAlign?: 'center' | 'right' | 'left';
}

const helpers = css<TypographyProps>`
  font-weight: ${({ theme }) => theme.styledVariant('weight', {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extraBold: 800,
    default: 400
  })};
  text-align: ${({ theme }) => theme.styledVariant('textAlign', {
    default: 'inherit',
    center: 'center',
    right: 'right',
    left: 'left',
  })};
`

const base = css<TypographyProps>`
  ${helpers}
  ${({ uppercase }) => uppercase && 'text-transform: uppercase;' };
  ${({ capitalize }) => capitalize && 'text-transform: capitalize;' };

  color: ${({ theme, colorScheme }) => colorScheme ? theme.colorScheme[colorScheme].surface.text.base.primary : 'inherit'};

  @media (min-width: 992px) and (max-width: 1143px) {
    font-size: 80%;
  }
`

export const Heading = styled.div<TypographyProps>`
  ${base};
  ${({ theme, as }) => theme.typo(as || 'div')};
  ${({ theme, typo }) => theme.typo(typo || 'displaySM')};
  ${({ fontPrimary }) => fontPrimary ? getFontClassName(primaryFont) : null};
  ${({ fontPrimary }) => fontPrimary && process.env.STORYBOOK ? 'font-family: Everett;' : null};
  letter-spacing: -1px;

  .highlighted {
    padding: ${({ theme }) => theme.rem(0, 4)};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.highlight.text};
    border-radius: ${({ theme }) => theme.rem(4)};
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.highlight.background};
  }
`

export const HeadingUnderlined = styled.span`
  width: fit-content;
  margin: 0 auto;
`

export const Paragraph = styled.p<TypographyProps>`
  ${base};
  ${({ theme, typo }) => theme.typo(typo || 'paragraphMD')};
  ${({ fontPrimary }) => fontPrimary ? getFontClassName(primaryFont) : null};
  ${({ fontPrimary }) => fontPrimary && process.env.STORYBOOK ? 'font-family: Everett;' : null};

  a {
    color: inherit;
    background-image: linear-gradient(0deg, currentColor, currentColor);
    background-position: 100% 100%;
    background-repeat: no-repeat;
    background-size: 100% 1px;
    transition: background-size 0.3s ease-in-out;
    transform: translateZ(0);
    -webkit-font-smoothing: subpixel-antialiased;

    &:hover,
    &:focus {
      background-size: 0% 1px;
      outline-width: 0;
    }
  }

  b,
  strong {
    font-weight: 600;
    color: inherit;
  }

  em {
    font-style: italic;
  }

  ol, ul {
    margin-block-start: ${({ theme }) => theme.rem(8)};
    margin-block-end: ${({ theme }) => theme.rem(8)};
    padding-inline-start: ${({ theme }) => theme.rem(16)};
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }

  > p:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.rem(8)};
  }

  .highlighted {
    border-radius: ${({ theme }) => theme.rem(4)};
    padding: ${({ theme }) => theme.rem(0, 4)};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.highlight.text};
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.highlight.background};
  }
`

export const Label = styled.div<TypographyProps>`
  ${base};
  ${({ theme, typo }) => theme.typo(typo || 'label')};
  color: ${({ theme, colorScheme }) => colorScheme ? theme.colorScheme[colorScheme].surface.text.base.primary : 'inherit'};
`

export const ButtonLabel = styled.span<TypographyProps>`
  ${base};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  ${({ theme, typo }) => theme.typo(typo || 'buttonMD')};
  letter-spacing: 0.01em;
  pointer-events: none;
  z-index: 4;
  ${({ fontPrimary }) => fontPrimary ? getFontClassName(primaryFont) : null};
  ${({ fontPrimary }) => fontPrimary && process.env.STORYBOOK ? 'font-family: Everett;' : null};

  transition: color 0.4s ease-in-out;

  > i {
    vertical-align: middle; 
  }
`
